/* ============
 * Edit Page
 * ============
 *
 * This is the page for adding recipes.
 */
import { mapGetters, mapMutations } from 'vuex';
import productService from './../../../../services/product';
import recipeService from './../../../../services/recipe';
import recipeTypeService from './../../../../services/recipeType';
import materialService from './../../../../services/material';
import recipeIngredienceService from './../../../../services/recipeIngredience';
import recipeTransformer from './../../../../transformers/recipe';
import recipeIngredienceTransformer from './../../../../transformers/recipeIngredience';
import recipeProcedureTransformer from './../../../../transformers/recipeProcedure';
import recipeProcedureModal from '../../../../components/recipeProcedureModal/recipeProcedureModal.vue';
import equipmentService from './../../../../services/equipment';
import Forms from './../../../../utils/forms/forms';
import store from './../../../../store';
import bulkAddProductModal from './../../../../components/bulkAddProductModal/bulkAddProductModal.vue';

export default {
  data() {
    return {
      form: new Forms({
        name: {
          value: '',
          type: 'text',
        },
        notes: {
          value: '',
          type: 'textarea',
        },
        recipeTypeId: {
          value: '',
          type: 'select',
        },
      }),
      formProduct: new Forms({
        product: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
      }),
      formIngredience: new Forms({
        recipeId: {
          value: '',
          type: 'text',
        },
        materialId: {
          value: '',
          type: 'select',
        },
        amount: {
          value: '',
          type: 'text',
        },
      }),
      hiddenFields: ['recipeId'],
      loading: false,
      fetchedRecipe: null,
      addProductsButton: [
        {
          text: this.$t('table.orderProducts.addProductsButton'),
          action: this.showBulkAddProductModal,
        },
      ],
      addIngrediencesButton: [
        {
          text: this.$t('static.recipeIngrediences.index.addIngrediences'),
          action: this.showBulkAddIngredienceModal,
        },
      ],
    };
  },

  created() {
    Promise.all([
      recipeTypeService.all(),
      materialService.all(),
      equipmentService.all(),
    ]).then(() => {
      this.loading = false;
    });
    this.show();
  },

  computed: {
    ...mapGetters({
      recipeTypes: 'allRecipeTypes',
      materials: 'allMaterials',
      ingrediences: 'allRecipeIngrediences',
      procedures: 'allRecipeProcedures',
      products: 'allRecipeProducts',
    }),
    ingredienceList() {
      return this.ingrediences.map(ingredience =>
        Object.assign({}, ingredience)
      );
    },
    productList() {
      return this.products.map(products => Object.assign({}, products));
    },
    procedureList() {
      return this.procedures.map((procedure) => {
        const p = Object.assign({}, procedure);
        p.ingrediences = recipeIngredienceTransformer.fetchCollection(
          procedure.ingrediences
        );
        return p;
      });
    },
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      setModalButtonLoading: 'SET_MODAL_BUTTON_LOADING',
    }),

    showBulkAddProductModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        disableQuantity: true,
        fetchProductsMethod: this.fetchAllProductsBasic,
        onSave: this.handleBulkProductSave,
      });
      this.toggleModal(true);
    },

    fetchAllProductsBasic() {
      return Promise.all([
        productService.allBasic({
          filter: 'active',
          warehouse_id: '',
        }),
      ]);
    },

    handleBulkProductSave(selectedProducts) {
      const products = selectedProducts.map(p => ({ product_id: p.id }));
      this.formProduct.loading = true;
      return recipeService
        .bulkStoreProduct(this.fetchedRecipe.id, products)
        .then(() => {
          this.formProduct.loading = false;
        });
    },

    showBulkAddIngredienceModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        fetchProductsMethod: this.fetchAllMaterialsBasic,
        onSave: this.handleBulkIngrediencesSave,
      });
      this.toggleModal(true);
    },

    fetchAllMaterialsBasic() {
      return Promise.all([
        productService.allBasic({
          filter: 'active',
          type: 'material',
        }),
      ]);
    },

    handleBulkIngrediencesSave(selectedProducts) {
      const products = selectedProducts.map(p => ({
        materialId: p.id,
        amount: p.qty,
      }));
      this.formIngredience.loading = true;
      return recipeIngredienceService
        .bulkStore(this.fetchedRecipe.id, products)
        .then(() => {
          this.formIngredience.loading = false;
        });
    },
    /**
     * Search remote function
     */
    searchMethod(search) {
      return Promise.all([
        productService.search({
          search,
        }),
      ]);
    },
    /**
     * Method to update the product group.
     */
    update() {
      this.form.loading = true;
      const recipe = recipeTransformer.send(this.form.data());
      recipeService
        .update(store.state.route.params.id, recipe)
        .then(() => {
          this.form.loading = false;
          this.$message({
            message: this.$t('static.recipes.edit.success'),
            type: 'success',
          });
          this.$router.push({
            name: 'production.recipes.index',
          });
        })
        .catch((errors) => {
          this.form.loading = false;
          this.form.recordErrors(errors);
        });
    },

    /**
     * Fetches the recipe object for editing.
     */
    show() {
      this.loading = true;
      recipeService.show(store.state.route.params.id).then((recipe) => {
        this.fetchedRecipe = recipe;
        this.form.assignData(recipe);
        const recipeIngrediences = recipeIngredienceTransformer.fetchCollection(
          recipe.ingrediences
        );
        store.dispatch('fetchAllRecipeIngrediences', recipeIngrediences);
        const recipeProcedures = recipeProcedureTransformer.fetchCollection(
          recipe.procedures
        );
        store.dispatch('fetchAllRecipeProcedures', recipeProcedures);
        store.dispatch('fetchAllRecipeProducts', recipe.products);
        this.loading = false;
      });
    },

    camalize(str) {
      return str
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
    },

    saveProduct() {
      this.formProduct.loading = true;
      return recipeService
        .storeProduct(this.fetchedRecipe.id, this.formProduct.data())
        .then(() => {
          this.formProduct.loading = false;
        })
        .catch((errors) => {
          this.formProduct.loading = false;
          this.formProduct.recordErrors(errors);
          const errorsKeys = Object.keys(this.formProduct.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    destroyProduct(id) {
      this.destroyLoading = true;
      recipeService
        .destroyProduct(this.fetchedRecipe.id, id)
        .then(() => {
          this.destroyLoading = false;
        })
        .catch(() => {
          this.destroyLoading = false;
        });
    },

    onFormIngredienceChange(name, value) {
      this.form.clearSuccess(name, value);
      this.form.clearErrors(name);
      this.form[name] = value;
      this.$forceUpdate();
    },

    saveIngredience() {
      this.formIngredience.loading = true;
      const data = Object.assign(this.formIngredience.data(), {
        recipeId: this.fetchedRecipe.id,
      });
      return recipeIngredienceService
        .store(data)
        .then(() => {
          this.formIngredience.loading = false;
        })
        .catch((errors) => {
          this.formIngredience.loading = false;
          this.formIngredience.recordErrors(errors);
          const errorsKeys = Object.keys(this.formIngredience.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },
    updateIngredience(ingredienceId) {
      this.formIngredience.loading = true;
      const data = Object.assign(this.formIngredience.data(), {
        recipeId: this.fetchedRecipe.id,
      });
      return recipeIngredienceService
        .update(ingredienceId, data)
        .then(() => {
          this.formIngredience.loading = false;
        })
        .catch((errors) => {
          this.formIngredience.loading = false;
          this.formIngredience.recordErrors(errors);
          const errorsKeys = Object.keys(this.formIngredience.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    destroyIngredience(id) {
      this.$confirm(
        this.$t('static.recipeIngrediences.index.confirmDelete'),
        this.$t('static.recipeIngrediences.index.confirmDeleteTitle'),
        {
          confirmButtonText: this.$t('button.delete'),
          cancelButtonText: this.$t('button.cancel'),
          type: 'warning',
        }
      )
        .then(() => {
          this.destroyLoading = true;
          recipeIngredienceService
            .destroy(id)
            .then(() => {
              this.destroyLoading = false;
            })
            .catch(() => {
              this.destroyLoading = false;
            });
        })
        .catch(() => {});
    },

    addProcedure() {
      this.setModalContent(() => recipeProcedureModal);
      this.setModalSize('full');
      this.toggleModal(true);
      this.setModalProps({
        recipeId: store.state.route.params.id.toString(),
      });
    },

    editProcedure(procedure) {
      this.setModalContent(() => recipeProcedureModal);
      this.setModalSize('full');
      this.toggleModal(true);
      this.setModalProps({
        recipeId: store.state.route.params.id.toString(),
        procedure,
      });
    },
  },

  watch: {
    recipeTypes(types) {
      this.form.setOptions(
        'recipeTypeId',
        types.map(c => ({
          id: c.id,
          name: this.$t(`static.recipes.types.${this.camalize(c.name)}`),
        }))
      );
    },
    materials(materials) {
      this.formIngredience.setOptions(
        'materialId',
        materials.map(c => ({
          id: c.id,
          name: `${c.name} ${c.unitMeasure !== '' ? `(${c.unitMeasure})` : ''} - ID: ${c.id}`,
        }))
      );
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
    VFormTable: require('./../../../../components/formTable/formTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/recipes/edit/edit.js